import type { VisibilityRule } from './visibility';
import {
  FIELD_PILL_COMPACT_LAYER,
  FIELD_PILL_LAYER,
  buildPillTextField,
  pillIconImageExpression,
  pillSortKeyExpression,
} from '../pillSprite';
// AK-211: слой «Рельеф» — choropleth по классу уклона. Цвет-выражение и
// data-encoding hex'ы живут в whitelisted colorMaps (не в registry inline).
import { slopeFillColorExpression } from '@/lib/colorMaps/slopeColors';
// R4: бренд-акцент карты — единый источник в whitelisted colorMaps.
import {
  MAP_BRAND_GREEN,
  MAP_NO_DATA_GREY,
  MAP_OUTLINE_CASING,
  MAP_OUTLINE_FIELD,
  MAP_SELECTION_YELLOW,
} from '@/lib/colorMaps/mapBrand';

export type LayerKind = 'raster' | 'line' | 'fill' | 'symbol';

/**
 * Layer config — declarative only (spec §6.1, §6.4 — Sprint 2026-21 T6).
 *
 * INVARIANT: registry entries describe layer shape + a *pure* predicate over
 * MapState. No `dynamicPaint` / `dynamicLayout` / lifecycle hooks live here —
 * paint is static, dynamic filters / source-URL swaps are owned by effects in
 * MapPageV2 (§6.4 layer ownership table).
 *
 * Spec §6.4: «Никаких dynamicPaint, никаких setLayoutProperty out of useEffect,
 * никаких lifecycle hooks в registry.»
 */
export interface LayerConfig {
  id: string;
  z: number;
  kind: LayerKind;
  source: string;
  sourceLayer?: string;
  filter?: unknown[];
  paint?: Record<string, unknown>;
  layout?: Record<string, unknown>;
  visibility: VisibilityRule;
  minzoom?: number;
  maxzoom?: number;
}

// Map-element accent (reference contours, zone outlines, focused highlights,
// map CTA-link). R4: значение вынесено в `@/lib/colorMaps/mapBrand`
// (MAP_BRAND_GREEN) — единый источник бренд-акцента карты. Посевные контуры
// поля (`outline-planting`) с AK-603 красятся отдельной константой
// `MAP_OUTLINE_FIELD` (маджента) — per-field `outline_color` из MVT больше не
// читаем, см. докстринг `MAP_OUTLINE_FIELD`.
const BRAND_GREEN = MAP_BRAND_GREEN;

/**
 * AK-516 — MVT source-layer «контуров поля» (зоны, backend `field_subzones`).
 * Слой per-LEAF: одна feature на зону, атрибуты по ключу (field_id, subzone_id).
 */
const ZONES_SRC_LAYER = 'field_subzones_styled';

/**
 * AK-516 — фильтр «это полигон ПОЛЯ, а не эталонный контур».
 *
 * Бэкенд отдавал `contour_type='planting'`, хотя рисовал `fields.geometry_3857`
 * — имя врало (посевных контуров в этом слое нет, они отдельная фаза AK-517).
 * Теперь отдаёт `'field'`. Матчим ОБА значения: тайлы кэшируются на 24 ч
 * (`Cache-Control: max-age=86400`), поэтому сразу после деплоя у части
 * пользователей в памяти MapLibre/edge-кэше ещё лежат тайлы со старым именем —
 * с жёстким `== 'field'` у них бы пропали заливка, «Рельеф» и КЛИК по полю.
 */
const IS_FIELD_POLY = ['in', ['get', 'contour_type'], ['literal', ['field', 'planting']]];

/**
 * AK-516 — «у поля нет активных зон».
 *
 * Поле с зонами красится ПОЛЕВЫМИ слоями в цвет ОДНОГО случайного leaf'а
 * (`_FVA_ONE_PER_FIELD` на бэке берёт одну строку MV на поле) — круг и углы
 * получали один цвет. Правило (вердикт architect по AK-516): приоритет по
 * каждому leaf'у — геометрия зоны, если активна в сезоне, иначе эталон поля.
 * Поэтому заливки поля гасим там, где зоны рисуют себя сами; контур и
 * click-target поля остаются (клик по зоне по-прежнему выбирает поле).
 *
 * `!=` (а не `['!', ['get',…]]`) — на легаси-тайлах свойства нет вовсе,
 * `['get']` вернёт null, и `null != true` даёт прежнее поведение.
 */
const HAS_NO_ZONES = ['!=', ['get', 'has_zones'], true];

/**
 * AK-516 R2 — «у зоны есть свой цвет NDVI / однородности».
 *
 * Ревью head-web (блокер 1): `zone-avg-ndvi-fill` / `zone-uniformity-fill`
 * были единственными zone-слоями вообще без `filter` — серый fallback
 * (`MAP_NO_DATA_GREY`) заливал поле в ОСНОВНОМ NDVI-режиме, пока полевой
 * близнец погашен `HAS_NO_ZONES`. Корень чинит бэкенд (зона без своей строки
 * MV берёт поле-leaf строку), это — страховка на уровне стиля: «нет данных»
 * остаётся сигналом ПОЛЕВОГО слоя, а зона без цвета просто не красится.
 *
 * `has` (а не `!= null`): MVT не кладёт в feature свойства со значением NULL,
 * поэтому отсутствие ключа — единственная честная проверка.
 */
const HAS_ZONE_NDVI = ['has', 'avg_ndvi_color'];
const HAS_ZONE_UNIFORMITY = ['has', 'cv_ndvi_color'];

// ──────────────────────────────────────────────────────────────────────────
// AK-603 — читаемость контура поля (UAT founder, staging v1.1637).
//
// Жалобы: (1) «контур пропадает на разных зумах», (2) «границы еле видны на
// спутнике». Оба — про ОДИН стек:
//
//   Корень (1): контуры стояли в САМОМ НИЗУ витринного стека (z=3/4), под
//   всеми заливками и растрами. Любой слой выше рисуется ПО НИМ:
//   NDVI-растр (z=5, opacity .85) — но он существует только на z ≥ 9
//   (`RASTER_MINZOOM`, v2.tsx), поэтому контур был виден на обзорных зумах и
//   исчезал при приближении; та же история с «Рельефом» (z=6, .6),
//   avg-ndvi/uniformity (z=7/8, .65), маской облаков (z=10, .6) и заливкой по
//   культуре (z=11, .45–.6). Комментарий у crop-fill прямо декларирует
//   «brand-контур остаётся виден» — намерение было, порядок слоёв ему
//   противоречил. Лечение: контуры переезжают НАД заливками (z≈11.4),
//   оставаясь под подписями (12.5+) и под жёлтым выделением.
//
//   Корень (2): ширина 0.8–2.4 px. На пёстром снимке линия такой толщины
//   тонет — фон под ней меняется быстрее, чем глаз ловит границу. Лечение —
//   casing (тёмная широкая линия ПОД яркой) + подъём базовой ширины. Casing
//   даёт линии собственный фон, не завися от того, светлая под ней стерня или
//   тёмная пашня (тот же приём, что halo у подписей).
//
// Ширины держим ОДНИМ источником: базовые стопы + производные (casing/выделение),
// чтобы правка толщины не разъезжалась по четырём paint-блокам.
const OUTLINE_ZOOM_STOPS: readonly (readonly [number, number])[] = [
  [7, 1.4],
  [10, 2],
  [13, 2.6],
  [16, 3.4],
  [18, 4],
];

/** Casing шире яркой линии на эту величину (px) с каждой стороны суммарно. */
const OUTLINE_CASING_EXTRA_PX = 1.6;
/** Жёлтое выделение обязано оставаться заметно толще обычного контура. */
const SELECTED_OUTLINE_EXTRA_PX = 2.2;

/**
 * `interpolate`-выражение ширины линии: базовые стопы + постоянная добавка.
 *
 * Инвариант (гардируется `field-contour-visibility.test.ts`): на всём видимом
 * диапазоне z8–18 ширина монотонно растёт и НИКОГДА не уходит в 0 — «дыры»
 * вида width→0 на интерполяции и есть тот самый «контур пропал на этом зуме».
 */
function outlineWidthExpr(extraPx = 0): unknown[] {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    ...OUTLINE_ZOOM_STOPS.flatMap(([z, w]) => [z, w + extraPx]),
  ];
}

/** Тёмная подложка-обводка под ярким контуром — читаемость на снимке. */
const OUTLINE_CASING_PAINT = {
  'line-color': MAP_OUTLINE_CASING,
  'line-width': outlineWidthExpr(OUTLINE_CASING_EXTRA_PX),
  // Полупрозрачно: на обзорных зумах сплошная чёрная кайма превратила бы
  // карту в сетку. 0.55 хватает, чтобы яркая линия читалась поверх снимка.
  'line-opacity': 0.55,
  'line-blur': 0.4,
};

/**
 * Layer registry per spec §6.1 — declarative predicates only.
 *
 * Each layer's `visibility.predicate` is a pure function of MapState. Any
 * runtime concern (filter sync, source URL swap, mutex with sibling layer) is
 * expressed via the predicate — no callbacks back into the layer at apply
 * time.
 *
 * Mutual exclusion (§6.1): crop-fill is visible iff `cropFillOn AND
 * activeRasterMode === 'none'`. When a raster product is active, crop-fill is
 * hidden entirely. Replaces the previous opacity-dimming via dynamicPaint.
 */
export const LAYER_REGISTRY: LayerConfig[] = [
  {
    id: 'basemap',
    z: 1,
    kind: 'raster',
    source: 'basemap',
    visibility: 'always',
    minzoom: 0,
    maxzoom: 22,
  },
  // Layer 1.5 — transport / labels overlay для hybrid-режима.
  // Bug C (Sprint 2026-19 Day 2): hybrid basemap раньше был stub'ом
  // (= satellite, TODO marker). Теперь — real composite: satellite
  // (z=1, basemap source) + transport overlay (z=1.5, Esri Reference
  // World_Transportation). Source `basemap-transport` активируется
  // visibility-предикатом s.basemap === 'hybrid', другие basemap
  // режимы (satellite, streets) рендерятся sole.
  {
    id: 'basemap-transport',
    z: 1.5,
    kind: 'raster',
    source: 'basemap-transport',
    paint: { 'raster-opacity': 0.85 },
    visibility: { predicate: (s) => s.basemap === 'hybrid' },
    minzoom: 0,
    maxzoom: 22,
  },
  // Layer 1.6 — places overlay (admin borders + city/town/locality labels)
  // для hybrid-режима. Founder UAT 2026-05-08: hybrid was missing place
  // names — World_Transportation alone shows roads, не показывает названия
  // населённых пунктов и пропускает streets в small towns. World_Boundaries_
  // and_Places — transparent overlay с labels поверх transport. z=1.6 (выше
  // transport, чтобы labels не перекрывались дорогами).
  {
    id: 'basemap-places',
    z: 1.6,
    kind: 'raster',
    source: 'basemap-places',
    paint: { 'raster-opacity': 0.95 },
    visibility: { predicate: (s) => s.basemap === 'hybrid' },
    minzoom: 0,
    maxzoom: 22,
  },
  // Layer 2 — RGB true-colour scene (raster, behind contours).
  // Source `rgb-scene-raster` swapped on selectedDate change in MapPageV2.
  {
    id: 'rgb-scene',
    z: 2,
    kind: 'raster',
    source: 'rgb-scene-raster',
    visibility: { predicate: (s) => s.rgbSceneOn && s.selectedDate !== null },
    minzoom: 7,
    maxzoom: 18,
  },
  // Слои 3/4 (контуры полей) ПЕРЕЕХАЛИ наверх стека — см. AK-603 у
  // `OUTLINE_ZOOM_STOPS` и блок «КОНТУРЫ ПОЛЯ» перед z=11.4х. На z=3/4 они
  // оказывались под каждой заливкой и под NDVI-растром, который существует
  // только с z ≥ 9 — отсюда «контур пропадает на разных зумах».
  // Layer 5 — NDVI raster products. Single shared raster layer, source URL
  // swapped by activeRasterMode/selectedDate in MapPageV2 (saves on style
  // churn). Backend INDICES has only ndvi/ndvi_contrast/gnr as raster
  // endpoints; avg_ndvi and uniformity are vector fill layers (7/8 below).
  {
    id: 'ndvi-active',
    z: 5,
    kind: 'raster',
    source: 'ndvi-active-raster',
    paint: { 'raster-opacity': 0.85 },
    visibility: {
      predicate: (s) =>
        (s.activeRasterMode === 'ndvi' ||
          s.activeRasterMode === 'ndvi_contrast' ||
          s.activeRasterMode === 'gnr') &&
        s.selectedDate !== null,
    },
    minzoom: 7,
    maxzoom: 18,
  },
  // Layer 6 — AK-211 «Рельеф»: choropleth-заливка полей по классу уклона
  // (vector fill, НЕ растр). Читает `slope_mean` (° из MVT `fields_styled`,
  // styled-v2 — LEFT JOIN field_terrain на бэке) и красит в один из 4 классов
  // (0–2/2–5/5–10/>10°); NULL (нет данных / не посчитано) → нейтральный серый.
  //
  // Отдельная ось от satellite: visibility = `s.terrainOn`. Взаимное исключение
  // с raster гарантируется тем, что crop-fill/avg-ndvi/uniformity-предикаты
  // проверяют свои условия независимо; чтобы терраин не стекался ПОД raster,
  // прячем его когда активен любой raster-режим (raster рисуется поверх, z=5<6,
  // но семантически «Рельеф» и NDVI — взаимоисключающие витрины). Терраин
  // работает в любом scope (вектор, без farm-lock спутников).
  {
    id: 'terrain-fill',
    z: 6,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    // Только полигон поля (reference дублирует те же поля — иначе двойная
    // заливка). Совпадает с логикой crop-fill/click-target.
    // AK-516: «Рельеф» — свойство ПОЛЯ (field_terrain по field_id), поэтому
    // has_zones его НЕ гасит: у зон нет своего уклона, показываем полевой.
    filter: IS_FIELD_POLY,
    paint: {
      'fill-color': slopeFillColorExpression(),
      'fill-opacity': 0.6,
      'fill-outline-color': BRAND_GREEN,
    },
    visibility: {
      predicate: (s) => s.terrainOn && s.activeRasterMode === 'none',
    },
    minzoom: 7,
    maxzoom: 22,
  },
  // Layer 7 — Avg NDVI per-field fill (vector, NOT raster). Backend Task 1.2
  // ships `avg_ndvi_color` via MVT `fields_styled` from the
  // `field_visual_attrs` materialised view.
  {
    id: 'avg-ndvi-fill',
    z: 7,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    // AK-516: поле с зонами красит слой зон (у каждой свой avg_ndvi_color).
    filter: HAS_NO_ZONES,
    paint: {
      'fill-color': ['coalesce', ['get', 'avg_ndvi_color'], MAP_NO_DATA_GREY],
      'fill-opacity': 0.65,
      'fill-outline-color': BRAND_GREEN,
    },
    visibility: { predicate: (s) => s.activeRasterMode === 'avg_ndvi' },
    minzoom: 7,
    maxzoom: 22,
  },
  // Layer 8 — Uniformity (CV NDVI) per-field fill (vector). Backend ships
  // `cv_ndvi_color` via MVT `fields_styled`.
  {
    id: 'uniformity-fill',
    z: 8,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    // AK-516: см. avg-ndvi-fill — CV зоны считается по её же строке.
    filter: HAS_NO_ZONES,
    paint: {
      'fill-color': ['coalesce', ['get', 'cv_ndvi_color'], MAP_NO_DATA_GREY],
      'fill-opacity': 0.65,
      'fill-outline-color': BRAND_GREEN,
    },
    visibility: { predicate: (s) => s.activeRasterMode === 'uniformity' },
    minzoom: 7,
    maxzoom: 22,
  },
  // Layer 10 — cloud mask (semi-transparent, always above NDVI when on).
  {
    id: 'cloud-mask',
    z: 10,
    kind: 'raster',
    source: 'cloud-mask-raster',
    paint: { 'raster-opacity': 0.6 },
    visibility: { predicate: (s) => s.cloudMaskOn && s.selectedDate !== null },
    minzoom: 7,
    maxzoom: 18,
  },
  // Layer 11 — crop fill, split per current_phase (AK-104 founder UAT 2026-05-08).
  // Per canonical spec §1 / 2026-05-07-domain-model-canonical.md:
  //   planned   → diagonal stripe pattern (`crop-stripe-{crop_id}`)
  //   growing   → solid fill (current_crop fill_color)
  //   harvested → dots pattern         (`crop-dots-{crop_id}`)
  //   fallow    → solid brown          (handled inline by fill_color from MV)
  //
  // V2 раньше имел single 'crop-fill' layer без phase split (Sprint 2026-21
  // T13 simplification). Founder UAT поймал что подсолнечник «запланирован»
  // рендерится сплошной заливкой как «растёт» — pattern-by-phase logic потеряна.
  // Restored здесь как 3 sub-layers с одинаковым visibility predicate.
  //
  // Pattern images регистрируются runtime через `buildCropPatternAtlas`
  // (frontend-vite/src/lib/map/cropPatterns.ts) — вызывается в v2.tsx
  // после map.load + /scope/crops fetch. Если pattern не зарегистрирован
  // (race / fetch fail), MapLibre silently рисует transparent для этого
  // полигона — `fallback-color` paint property guarantees visible solid.
  //
  // Mutual exclusion via predicate (spec §6.1):
  //   visible iff `cropFillOn AND activeRasterMode === 'none'`.
  {
    id: 'crop-fill-planned',
    z: 11.0,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    filter: ['all', ['==', ['get', 'current_phase'], 'planned'], HAS_NO_ZONES],
    paint: {
      // AK-297: diagonal-stripe pattern (planned). 0.55 — pattern читается
      // поверх satellite без забивания снимка (см. growing 0.45).
      'fill-pattern': ['concat', 'crop-stripe-', ['to-string', ['get', 'current_crop_id']]],
      'fill-opacity': 0.55,
    },
    visibility: {
      predicate: (s) => s.cropFillOn && s.activeRasterMode === 'none' && !s.terrainOn,
    },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'crop-fill-growing',
    z: 11.1,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    filter: [
      'all',
      [
        'any',
        ['==', ['get', 'current_phase'], 'growing'],
        ['==', ['get', 'current_phase'], 'fallow'],
        // Backwards-compat для записей до миграции 025 (current_phase NULL):
        ['!', ['has', 'current_phase']],
      ],
      HAS_NO_ZONES,
    ],
    paint: {
      // AK-297: solid per-crop fill (fill_color from field_visual_attrs MV).
      // Opacity 0.45 — заливка по культуре читается, но satellite-подложка и
      // brand-контур остаются видны (design review: 0.55 «забивал» снимок).
      'fill-color': ['coalesce', ['get', 'fill_color'], MAP_NO_DATA_GREY],
      'fill-opacity': 0.45,
    },
    visibility: {
      predicate: (s) => s.cropFillOn && s.activeRasterMode === 'none' && !s.terrainOn,
    },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'crop-fill-harvested',
    z: 11.2,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    filter: ['all', ['==', ['get', 'current_phase'], 'harvested'], HAS_NO_ZONES],
    paint: {
      // AK-297: dots pattern (harvested). 0.6 — dots остаются заметны, но не
      // плотнее growing-заливки (consistency со стеком crop-fill).
      'fill-pattern': ['concat', 'crop-dots-', ['to-string', ['get', 'current_crop_id']]],
      'fill-opacity': 0.6,
    },
    visibility: {
      predicate: (s) => s.cropFillOn && s.activeRasterMode === 'none' && !s.terrainOn,
    },
    minzoom: 7,
    maxzoom: 22,
  },
  // ──────────────────────────────────────────────────────────────────────
  // AK-516 / ADR-058 D5.3 (i-b) — «КОНТУРЫ ПОЛЯ» (зоны, `field_subzones`).
  //
  // Зеркало полевых заливок, но на per-LEAF source-layer'е: каждая зона несёт
  // СВОИ fill_color / current_phase / avg_ndvi_color (джойн MV по полному
  // ключу (field_id, subzone_id) на бэке). Полевые слои-близнецы погашены
  // на этих полях через HAS_NO_ZONES — так круг и углы перестают краситься
  // одним цветом (баг: per-field слой берёт одну случайную leaf-строку).
  //
  // Предикаты видимости — БЕЗ нового тоггла: зона показывается ровно тогда,
  // когда показалась бы заливка поля. Отдельный переключатель добавил бы
  // состояние, в котором поле не покрашено ничем.
  //
  // Окно активности зоны (сезон) фильтруется на бэке по season_id в URL
  // тайла — смена сезона пересоздаёт source (v2.tsx), см. CLAUDE.md
  // «Регрессии MVT-фильтра».
  {
    id: 'zone-avg-ndvi-fill',
    z: 7.1,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: ZONES_SRC_LAYER,
    // AK-516 R2 (блокер 1, head-web): без фильтра слой красил серым ЛЮБУЮ
    // зону, у которой атрибуты пришли пустыми — а полевой близнец в этот
    // момент погашен HAS_NO_ZONES. Итог: поле теряло цвет в основном
    // NDVI-режиме. Бэк чинит корень (фолбэк зоны на поле-leaf строку MV),
    // здесь — страховка: «нет данных» — полевой сигнал, зона без своего цвета
    // просто не красится, а не кладёт серое пятно на всё поле.
    filter: HAS_ZONE_NDVI,
    paint: {
      'fill-color': ['coalesce', ['get', 'avg_ndvi_color'], MAP_NO_DATA_GREY],
      'fill-opacity': 0.65,
      'fill-outline-color': BRAND_GREEN,
    },
    visibility: { predicate: (s) => s.activeRasterMode === 'avg_ndvi' },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'zone-uniformity-fill',
    z: 8.1,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: ZONES_SRC_LAYER,
    // AK-516 R2: см. zone-avg-ndvi-fill — та же страховка от серого пятна.
    filter: HAS_ZONE_UNIFORMITY,
    paint: {
      'fill-color': ['coalesce', ['get', 'cv_ndvi_color'], MAP_NO_DATA_GREY],
      'fill-opacity': 0.65,
      'fill-outline-color': BRAND_GREEN,
    },
    visibility: { predicate: (s) => s.activeRasterMode === 'uniformity' },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'zone-crop-fill-planned',
    z: 11.3,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: ZONES_SRC_LAYER,
    filter: ['==', ['get', 'current_phase'], 'planned'],
    paint: {
      'fill-pattern': ['concat', 'crop-stripe-', ['to-string', ['get', 'current_crop_id']]],
      'fill-opacity': 0.55,
    },
    visibility: {
      predicate: (s) => s.cropFillOn && s.activeRasterMode === 'none' && !s.terrainOn,
    },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'zone-crop-fill-growing',
    z: 11.31,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: ZONES_SRC_LAYER,
    filter: [
      'any',
      ['==', ['get', 'current_phase'], 'growing'],
      ['==', ['get', 'current_phase'], 'fallow'],
      // Зона без занятия в этом сезоне (LEFT JOIN на бэке отдал NULL) — она
      // всё равно рисуется, нейтральным fallback-цветом ниже.
      ['!', ['has', 'current_phase']],
    ],
    paint: {
      'fill-color': ['coalesce', ['get', 'fill_color'], MAP_NO_DATA_GREY],
      'fill-opacity': 0.45,
    },
    visibility: {
      predicate: (s) => s.cropFillOn && s.activeRasterMode === 'none' && !s.terrainOn,
    },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'zone-crop-fill-harvested',
    z: 11.32,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: ZONES_SRC_LAYER,
    filter: ['==', ['get', 'current_phase'], 'harvested'],
    paint: {
      'fill-pattern': ['concat', 'crop-dots-', ['to-string', ['get', 'current_crop_id']]],
      'fill-opacity': 0.6,
    },
    visibility: {
      predicate: (s) => s.cropFillOn && s.activeRasterMode === 'none' && !s.terrainOn,
    },
    minzoom: 7,
    maxzoom: 22,
  },
  // Границы зон — тонкий пунктир поверх заливок, но ПОД click-target'ом
  // (11.5) и подписями. Всегда видимы: зона — это геометрический факт поля,
  // а не витрина. minzoom 11 — на обзорных зумах это шум.
  {
    id: 'zone-outline',
    z: 11.4,
    kind: 'line',
    source: 'fields-mvt',
    sourceLayer: ZONES_SRC_LAYER,
    paint: {
      // AK-516 R2 (правка head-web): цвет КОНСТАНТА, а не `outline_color` из MV.
      // Через MV граница мигала по состоянию посева (зона с посевом —
      // оранжевая, без — зелёная), хотя зона — геометрический факт поля, а не
      // витрина занятости. Заодно контур зоны перестаёт зависеть от того,
      // нашлась ли для неё leaf-строка MV.
      'line-color': BRAND_GREEN,
      'line-width': ['interpolate', ['linear'], ['zoom'], 11, 0.6, 16, 1.6],
      'line-dasharray': [2, 2],
    },
    visibility: 'always',
    minzoom: 11,
    maxzoom: 22,
  },
  // ──────────────────────────────────────────────────────────────────────
  // КОНТУРЫ ПОЛЯ (бывшие z=3/4) — AK-603.
  //
  // Порядок внутри группы: casing → яркая линия (иначе casing затрёт линию).
  // Группа целиком стоит НАД всеми заливками/растрами (крайний — crop-fill
  // z=11.2 и зоны z=11.32) и НАД пунктиром границ зон (11.4), но ПОД
  // click-target'ом (11.5, невидимый), жёлтым выделением (12.5) и подписями
  // (12.5х/12.6) — контур поля должен читаться поверх любой витрины, но не
  // затирать выбор и имена.
  //
  // Режимы взаимоисключающие (`contourMode`), поэтому в кадре одновременно
  // живёт только одна пара casing+линия.
  {
    id: 'outline-reference-casing',
    z: 11.42,
    kind: 'line',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    filter: ['==', ['get', 'contour_type'], 'reference'],
    paint: OUTLINE_CASING_PAINT,
    visibility: { predicate: (s) => s.contourMode === 'reference' },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'outline-reference',
    z: 11.43,
    kind: 'line',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    filter: ['==', ['get', 'contour_type'], 'reference'],
    paint: {
      'line-color': BRAND_GREEN,
      'line-width': outlineWidthExpr(),
    },
    visibility: { predicate: (s) => s.contourMode === 'reference' },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'outline-planting-casing',
    z: 11.44,
    kind: 'line',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    filter: IS_FIELD_POLY,
    paint: OUTLINE_CASING_PAINT,
    visibility: { predicate: (s) => s.contourMode === 'planting' },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'outline-planting',
    z: 11.45,
    kind: 'line',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    filter: IS_FIELD_POLY,
    paint: {
      // Боевой цвет (AK-603, решение founder 2026-08-15) — неоновая маджента,
      // константа `MAP_OUTLINE_FIELD` (`lib/colorMaps/mapBrand.ts`). Больше НЕ
      // берём per-field `outline_color` из MVT: в MV это мёртвая константа
      // '#FF6D00' без семантики (см. докстринг MAP_OUTLINE_FIELD). Casing
      // ниже даёт линии контраст на снимке.
      'line-color': MAP_OUTLINE_FIELD,
      'line-width': outlineWidthExpr(),
    },
    visibility: { predicate: (s) => s.contourMode === 'planting' },
    minzoom: 7,
    maxzoom: 22,
  },
  // Layer 11.5a (REMOVED, AK-428 founder 2026-07-16): 'fields-no-data-stripes'
  // — серая заливка «нет данных» поверх растра. Убрана: индикатор опирался на
  // статистику пайплайна (field_indices_v2), а не на реальные пиксели растра,
  // и при дырах пайплайна «туманил» валидный NDVI (P1 AK-428). Coverage-каунт
  // остался в UI-пилюле «X/Y полей» (coverageEffect) — карту не трогает.
  // Layer 11.5b — invisible fill click target.
  // Bug A (Sprint 2026-19 Day 2): outline lines (z=3/4) too thin для click
  // hit-test — pointer must hit pixel-perfect 1.6px line. Founder reports
  // "click on field does nothing". Решение: добавляем invisible (opacity 0)
  // fill поверх crop-fill / под labels — wide click target, всегда покрывает
  // полигон поля. queryRenderedFeatures по этому слою гарантированно
  // попадает в feature, даже если outline-планшета спрятан.
  // visibility: 'always' — слой невидим (opacity 0), но active для clicks.
  {
    id: 'field-click-target',
    z: 11.5,
    kind: 'fill',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    // AK-516: click-target намеренно НЕ фильтруется по has_zones — клик по
    // зоне обязан по-прежнему выбирать ПОЛЕ (попап — полевой).
    filter: IS_FIELD_POLY,
    paint: {
      'fill-color': '#000000',
      'fill-opacity': 0,
    },
    visibility: 'always',
    minzoom: 7,
    maxzoom: 22,
  },
  // Layer 12 — labels, split into 4 independent toggleable sublayers per spec
  // §1.12. Replaces the previous single `field-labels` symbol layer that used
  // a `format` text-field expression keyed off feature-state — that approach
  // never worked because LayerManager doesn't call `setFeatureState`. Each
  // sublayer now owns one toggle (state.labels.<key>) and renders one piece
  // of content (number / crop / icon / area). Float z-values 12.1–12.4 are
  // preserved by LayerManager.init's stable sort `(a, b) => a.z - b.z`.
  //
  // AK-101 founder UAT 2026-05-08: «При выборе отображения площади поля и
  // названия поля — остаётся площадь, а название затирается». Root cause:
  // `text-allow-overlap: false` на каждом sublayer'е. MapLibre's symbol
  // collision detection видит соседние sublayers (number/crop/area) на small
  // fields как overlap'ing друг с другом (даже с offset stacking) и
  // culls последующие layers. Fix: `text-allow-overlap: true` +
  // `text-ignore-placement: true` на всех 4. Labels рендерятся unconditionally
  // в их offset'ах. На очень маленьких полях это даёт визуальную кашу —
  // acceptable trade-off vs founder bug «зачем переключатель если ничего не показывает».
  // Stacking offsets уже spec'нуты так что labels вертикально не пересекаются:
  // crop (top, -0.6) → icon (center) → number (bottom, +0.6) → area (further bottom, +1.6).
  //
  // 2026-06-25: этот AK-101 подход (allow-overlap=true) — КАНОНИЧЕСКИЙ и
  // восстановлен после того как промежуточный revert на collision (false)
  // вызвал prod-freeze (см. PERF-CRITICAL ниже).

  // ⚠️ PERF-CRITICAL (incident 2026-06-25, founder-escalated): holding/cluster/
  // farm fitBounds landed at z11–13 over a holding с ТЫСЯЧАМИ полей (654k га,
  // dense sub-parcel grids). `field_centroids_styled` symbol layers ниже с
  // `text-allow-overlap: false` запускали MapLibre symbol-placement +
  // CollisionIndex pass над всем набором → main-thread starvation → render-loop
  // не мог скомпоновать кадр → БЕЛЫЙ canvas (map.isMoving() залипал true,
  // isStyleLoaded false). Live-эксперимент (Chrome): скрытие symbol-слоёв
  // → canvas рендерится МГНОВЕННО. Reference: legacy `setupMapLayers.ts:584,629`
  // — production /map использует `text-allow-overlap: true` на field_centroids
  // именно по этой причине ("Production /map использует allow-overlap=true").
  //
  // ДВА рычага, оба обязательны:
  //   (1) collision OFF — `text-allow-overlap: true` + `text-ignore-placement:
  //       true` → MapLibre НЕ строит CollisionIndex для этих слоёв (убирает
  //       O(n) collision-проход — главный блокер). Возврат к проверенному
  //       legacy-поведению (то же, что AK-101 fix, до ошибочного revert'а).
  //   (2) count bound через minzoom — per-field labels появляются только когда
  //       в кадре НЕМНОГО полей (юзер инспектирует конкретные поля), а не над
  //       целым холдингом. Farm-fit садится на ~z11–13 (observed), holding-jump
  //       на z13 — поэтому минимальный безопасный zoom для per-field labels =
  //       z14+ (там видно горстку полей, не весь scope). Ниже z14 ориентацию
  //       дают scope-centroid-labels (z6+) и arrays-pills (z7–12) с bounded
  //       server-filtered счётчиком фич.
  //
  // Progressive disclosure (обновлено):
  //   zoom < 14   — per-field labels скрыты (видны контуры + cropfill +
  //                 scope/array подписи)
  //   zoom ≥ 14   — иконка культуры
  //   zoom ≥ 15   — номер поля / название культуры / площадь
  //
  // «Mush» на zoom-out, которого боялся прежний revert на allow-overlap=false,
  // теперь предотвращён minzoom-гейтом (14/15), а НЕ collision-проходом —
  // поэтому freeze не возвращается, а каши на zoom-out нет (labels просто
  // скрыты пока не приблизишься к отдельным полям).

  // ──────────────────────────────────────────────────────────────────────
  // AK-299 / AK-303 — Field-label PILLS (icon-text-fit symbol).
  //
  // Подписи полей рендерятся pill-карточками (единый язык со scope-чипами
  // «Аргамак» и popup AK-295), а НЕ голым haloed-текстом. Подложка — один
  // stretchable rounded-rect спрайт `field-pill-bg`, зарегистрированный
  // runtime через `usePillSpriteEffect` (effects/pillSpriteEffect.ts →
  // installPillSprite). Спрайт остаётся на GPU: НЕ HTML-маркеры (перф/freeze).
  //
  // AK-590 (founder approve 16.08, вариант B) — ОДНА ПИЛЮЛЯ НА ПОЛЕ.
  //
  // Было: три symbol'а на поле (pill «номер · площадь» + pill «культура» +
  // отдельная иконка культуры). Две НЕПРОЗРАЧНЫЕ плашки при collision-OFF не
  // «накладывались», а СТИРАЛИ друг друга (founder UAT: «белая пилюля
  // полностью перекрывает подпись MA1-078.1»). Теперь на поле рисуется ровно
  // ОДНА пилюля:
  //   строка 1 — «{номер}   {площадь целыми га}»
  //   строка 2 — «{иконка культуры} {культура}» (когда тумблеры включены)
  //
  // Два уровня детализации задаются ДВУМЯ ВЗАИМОИСКЛЮЧАЮЩИМИ по зуму слоями —
  // `['zoom']` в layout-выражении style spec'ом запрещён (только в фильтре):
  //   - `field-pill-compact` — чип «только номер» на пороге поля
  //     (min_zoom_for_label ≤ zoom < min_zoom_for_label + 1). На пороге поле
  //     ~40px, а полная пилюля ~120–140px — корень наложений был именно в том,
  //     что подпись шире своего поля.
  //   - `field-pill` — полная двухстрочная пилюля с +1 зума.
  // Итог: symbol-слоёв над field_centroids_styled стало 2 (было 3), а
  // symbol'ов на поле в кадре — 1 (было до 3). Перф-бюджет карты улучшается.
  //
  // Выбранное поле — БЕЗ отдельного слоя: data-driven `symbol-sort-key`
  // (максимальный ключ → всегда поверх) + data-driven `icon-image` (подложка с
  // жёлтым бордером MAP_SELECTION_YELLOW). Оба выражения пересобирает
  // `syncPillSelection` из useLayerSyncEffect при смене selectedFieldId.
  //
  // ⚠️ FREEZE-ИНВАРИАНТЫ (architect, нарушение = white-canvas freeze
  // 2026-06-25): оба слоя — collision OFF. text/icon-allow-overlap:true +
  // text/icon-ignore-placement:true → MapLibre НЕ строит CollisionIndex
  // (убирает O(n) проход — главный блокер над холдингом с тысячами полей).
  // Число подписей ограничено per-field фильтром `min_zoom_for_label` (AK-134):
  // на z11 над холдингом проходят лишь единицы крупных полей, не тысячи мелких.
  //
  // ⚠️ AK-590 — `symbol-sort-key` = `area_ha` (БЕЗ инверсии). При
  // `allow-overlap: true` по style spec поверх рисуется фича с БОЛЬШИМ
  // sort-key. Прежнее `['-', 0, ['get','area_ha']]` давало крупному полю самый
  // ОТРИЦАТЕЛЬНЫЙ ключ — то есть работало ровно наоборот своему комментарию:
  // мелкие суб-дольки систематически затирали подпись крупного поля (кейс
  // MA1-078.1 из founder-UAT). Не инвертировать обратно.
  //
  // ⚠️ ТОКЕНЫ/ТЕМА: фон+бордер несёт спрайт (theme-aware re-register). Цвет
  // ТЕКСТА здесь — light-fallback; installPillSprite переустанавливает его из
  // computed-токенов (`--foreground` / `--muted-foreground`) на load + смене
  // темы (registry статичен, MapLibre не резолвит hsl(var(--token))). Halo нет
  // — подложка pill заменяет.

  // Layer 12.e — Компактный ЧИП (только номер) на пороге поля.
  // Диапазон: min_zoom_for_label ≤ zoom < min_zoom_for_label + 1. На своём
  // пороге поле ~40px (backend `_LABEL_MIN_PX`), поэтому здесь допустима лишь
  // короткая секция — она влезает в поле, за которое отвечает.
  {
    id: FIELD_PILL_COMPACT_LAYER,
    // z как у прежнего number-pill: выше жёлтого highlight (12.5), ниже
    // scope-centroid-labels (12.6).
    z: 12.51,
    kind: 'symbol',
    source: 'fields-mvt',
    sourceLayer: 'field_centroids_styled',
    // Взаимоисключающий с полной пилюлей диапазон — на поле в кадре ровно ОДИН
    // symbol. `min_zoom_for_label` в фильтре обязателен (freeze-инвариант,
    // registry-symbol-perf.test.ts).
    filter: [
      'all',
      ['<=', ['get', 'min_zoom_for_label'], ['zoom']],
      ['>', ['+', ['get', 'min_zoom_for_label'], 1], ['zoom']],
    ],
    layout: {
      // Light-fallback; theme-aware значение ставит installPillSprite.
      'text-field': buildPillTextField('#1C1917', '#78716C', {
        number: true,
        area: true,
        crop: true,
        icon: true,
      }, { compact: true }),
      'text-size': 12,
      'text-anchor': 'center',
      'text-padding': 2,
      // Выбранное поле — поверх остальных (sentinel до первой синхронизации).
      'symbol-sort-key': pillSortKeyExpression(null),
      // Pill-подложка под текстом (жёлтая — у выбранного поля).
      'icon-image': pillIconImageExpression(null),
      'icon-text-fit': 'both',
      // [top, right, bottom, left] — design-lead: гориз. padding 10px, верт. 3px.
      'icon-text-fit-padding': [3, 10, 3, 10],
      // FREEZE: collision OFF (text + icon).
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    visibility: {
      predicate: (s) => s.labels.number || s.labels.area || s.labels.crop || s.labels.icon,
    },
    minzoom: 4,
    maxzoom: 22,
  },
  // Layer 12.f — Полная двухстрочная пилюля с `min_zoom_for_label + 1`
  // (поле вдвое крупнее на экране — место под полный состав есть).
  //   строка 1: «{номер}   {площадь целыми га}»
  //   строка 2: «{иконка культуры} {культура}» — по тогглам.
  {
    id: FIELD_PILL_LAYER,
    z: 12.52,
    kind: 'symbol',
    source: 'fields-mvt',
    sourceLayer: 'field_centroids_styled',
    filter: ['<=', ['+', ['get', 'min_zoom_for_label'], 1], ['zoom']],
    layout: {
      'text-field': buildPillTextField('#1C1917', '#78716C', {
        number: true,
        area: true,
        crop: true,
        icon: true,
      }),
      'text-size': 12,
      'text-anchor': 'center',
      'text-justify': 'center',
      'text-line-height': 1.15,
      'text-padding': 2,
      'symbol-sort-key': pillSortKeyExpression(null),
      'icon-image': pillIconImageExpression(null),
      'icon-text-fit': 'both',
      // Две строки → верт. padding 4px (design-lead §5.3), гориз. 10px.
      'icon-text-fit-padding': [4, 10, 4, 10],
      // FREEZE: collision OFF (text + icon).
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    visibility: {
      predicate: (s) => s.labels.number || s.labels.area || s.labels.crop || s.labels.icon,
    },
    minzoom: 4,
    maxzoom: 22,
  },
  // Layer 12.c/12.d — Crop icon + Area label: слоёв БОЛЬШЕ НЕТ.
  // AK-299 свернул площадь в pill, AK-590 свернул туда же культуру и иконку
  // (image-секция внутри `format`, вторая строка). Тогглы `s.labels.area` /
  // `.crop` / `.icon` теперь открывают соответствующие секции ОДНОЙ пилюли —
  // см. `buildPillTextField` + `syncNumberAreaTextField`.

  // Sprint 2026-26: Centroid scope labels — Founder UAT 2026-05-08 lock-in.
  //
  // Visual labels (text only, halo, NO pill background) at the geographic
  // centroid of each scope-boundary (holding / company / cluster / farm /
  // brigade). Backend MV `tenant_<x>.scope_centroids` (migration 033) +
  // MVT endpoint `/api/v1/tiles/scope-centroids/{z}/{x}/{y}.mvt`.
  //
  // Per-zoom scope_type filtering is enforced server-side (backend handler
  // in tiles.py) — frontend just renders whatever feature comes through.
  // Server filter mapping (для reference):
  //   z 6-9   → holding only
  //   z 9-11  → +company +cluster
  //   z 11-14 → +farm
  //   z 14+   → +brigade
  //
  // Text-size interpolation per scope_type creates visual hierarchy:
  // holdings/companies — big bold; cluster — medium; farm/brigade — small.
  // 'text-allow-overlap=false' + halo = clean (no clutter when many scopes).
  // z=12.6 — выше field selected highlight (12.5), ниже arrays-pills (13)
  // чтобы scope-имена читались поверх field labels.
  {
    id: 'scope-centroid-labels',
    z: 12.6,
    kind: 'symbol',
    source: 'scope-centroids-mvt',
    sourceLayer: 'scope_centroids',
    layout: {
      'text-field': ['get', 'name'],
      // Per-scope_type size — bold/larger для верхних уровней.
      'text-size': [
        'match',
        ['get', 'scope_type'],
        'holding', 16,
        'company', 14,
        'cluster', 13,
        'farm', 11,
        'brigade', 10,
        12, // default
      ],
      'text-anchor': 'center',
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'text-padding': 4,
      'text-letter-spacing': 0.05,
    },
    paint: {
      // Per-scope_type color — accent green for holding (top-level), darker
      // grey gradient downward. Tested на satellite + hybrid backgrounds.
      'text-color': [
        'match',
        ['get', 'scope_type'],
        'holding', '#1A6B1A',
        'company', '#1F2937',
        'cluster', '#374151',
        'farm', '#4B5563',
        'brigade', '#6B7280',
        '#374151',
      ],
      'text-halo-color': '#FFFFFF',
      'text-halo-width': 2.5,  // strong halo для readability на satellite
      'text-halo-blur': 0.5,
    },
    visibility: 'always',  // Always visible (no toggle) — это визуальная ориентация.
    minzoom: 6,
    maxzoom: 22,
  },
  // Layer 12.5 — Selected field yellow highlight.
  // Bug A2 (Sprint 2026-19 Day 2): founder reports «клик по полю — контур
  // должен подсвечиваться жёлтым, сейчас не выделяется». Click handler в
  // v2.tsx уже dispatch'ит SET_SELECTED_FIELD (id из MVT property), reducer
  // обновляет state.selectedFieldId. Этот слой подхватывает selection через
  // visibility predicate + dynamic filter (set из v2.tsx через
  // map.setFilter, как сделано для tile sources — LayerManager не имеет
  // dynamicFilter API by design, не плодим механизмы). Жёлтый = #FACC15
  // (Tailwind yellow-400, brand-friendly), толщина line-width 3, blur 0.5
  // для smooth edges. z=12.5. AK-383 #7: field-label pills подняты на 12.51–12.53
  // (выше highlight) — пилюля названия читается ПОВЕРХ жёлтого контура, не под ним.
  // Highlight остаётся выше crop-fill/basemap и ниже scope-centroid-labels (12.6).
  {
    id: 'field-selected-highlight',
    z: 12.5,
    kind: 'line',
    source: 'fields-mvt',
    sourceLayer: 'fields_styled',
    // Initial filter — никогда не matches. Реальный filter применяется
    // через map.setFilter('field-selected-highlight', ['==', ['get', 'id'], <id>])
    // в useEffect v2.tsx когда selectedFieldId меняется.
    filter: ['==', ['get', 'id'], '__none__'],
    paint: {
      'line-color': MAP_SELECTION_YELLOW,
      // AK-603: константная 3 px после подъёма обычного контура (до 4 px на
      // z18) переставала читаться как «выбрано». Выделение теперь тоже
      // зум-зависимое и по построению шире контура на SELECTED_OUTLINE_EXTRA_PX
      // — выбранное поле остаётся отличимым на любом зуме (гард в тесте).
      'line-width': outlineWidthExpr(SELECTED_OUTLINE_EXTRA_PX),
      'line-blur': 0.5,
    },
    visibility: { predicate: (s) => s.selectedFieldId !== null },
    minzoom: 7,
    maxzoom: 22,
  },
  {
    id: 'arrays-pills',
    z: 13,
    kind: 'symbol',
    source: 'fields-mvt',
    sourceLayer: 'arrays_centroids',
    layout: {
      'text-field': ['coalesce', ['get', 'name'], ''],
      'text-size': 12,
      'text-anchor': 'center',
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#1A1A1A',
      'text-halo-color': '#FFFFFF',
      'text-halo-width': 1.5,
    },
    visibility: 'always',
    minzoom: 7,
    maxzoom: 12,
  },
];
